import type { Game } from "@rally/rating";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../components/AppShell.js";
import { MemberPicker } from "../components/MemberPicker.js";
import { ScorePad } from "../components/ScorePad.js";
import { Button, Chips, Notice, inputStyle } from "../components/ui.js";
import { hasRole, useAuth } from "../lib/auth.js";
import { confirmMatch, fetchMyMatches, formatScoreline, logMatch } from "../lib/matches.js";
import { fetchMembers } from "../lib/members.js";
import { FORMATS, SPORT_LABEL } from "../lib/sport.js";
import {
  MATCH_CATEGORY_LABEL,
  deriveMatchCategory,
  matchRates,
  type Kind,
  type MatchFormat,
  type MemberCategory,
} from "../lib/types.js";

type Step = "setup" | "score" | "review";

const LAST_USED_KEY = "rally.lastKind";

/**
 * The critical path — requirements §5.2. Target is under 20 seconds, courtside, on a
 * phone.
 *
 * One app per sport removed the first question entirely, and the format defaults to
 * whatever you logged last — so the usual case opens straight onto picking players.
 */
export function LogMatch() {
  const { member } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [kind, setKind] = useState<Kind>(readLastKind());
  const [format, setFormat] = useState<MatchFormat>(readLastFormat());
  const [custom, setCustom] = useState(!isPreset(readLastFormat()));

  /**
   * Which category you are setting up. This filters the picker and nothing else — the
   * match's own category is derived by the database from who actually plays, so this can
   * never mislabel anything. Defaults to your own, since that is what you log most.
   */
  const [wanted, setWanted] = useState<MemberCategory | "mixed" | "any">(member?.category ?? "any");
  const [step, setStep] = useState<Step>("setup");
  const [sideA, setSideA] = useState<string[]>(member ? [member.id] : []);
  const [sideB, setSideB] = useState<string[]>([]);
  const [games, setGames] = useState<Game[]>([]);

  const { data: members = [] } = useQuery({ queryKey: ["members"], queryFn: fetchMembers });
  const { data: myMatches = [] } = useQuery({
    queryKey: ["my-matches", member?.id],
    queryFn: () => fetchMyMatches(member!.id),
    enabled: Boolean(member),
  });

  // Everyone you have shared a court with lately, most recent first.
  const recent = useMemo(() => {
    const seen: string[] = [];
    for (const match of myMatches) {
      for (const participant of match.participants) {
        if (participant.memberId !== member?.id && !seen.includes(participant.memberId)) {
          seen.push(participant.memberId);
        }
      }
    }
    return seen;
  }, [myMatches, member?.id]);

  const perSide = kind === "singles" ? 1 : 2;
  const nameOf = useCallback(
    (id: string) => members.find((m) => m.id === id)?.displayName ?? "…",
    [members],
  );
  const teamName = (side: string[]) => (side.length === 0 ? "—" : side.map(nameOf).join(" & "));

  const rosterReady = sideA.length === perSide && sideB.length === perSide;

  // Mixed needs everyone available; men's and women's narrow to that category. A member
  // with no category is offered under "any" only — putting them in a filtered list would
  // claim something about them that nobody has said.
  //
  // A member without a login is never offered at all. Nobody could confirm or dispute a
  // casual match for them, and the database refuses one outright; offering them here would
  // only be a way to find that out after typing the score.
  const eligible = useMemo(() => {
    const loggable = members.filter((m) => !m.managed);
    if (wanted === "any" || wanted === "mixed") return loggable;
    return loggable.filter((m) => m.category === wanted);
  }, [members, wanted]);

  // What the database will record, computed from the same rule it uses. Shown on review so
  // the answer cannot surprise anyone after the fact.
  const derived = useMemo(() => {
    const chosen = [...sideA, ...sideB].map(
      (id) => members.find((m) => m.id === id)?.category ?? null,
    );
    return chosen.length === 0 ? null : deriveMatchCategory(chosen);
  }, [sideA, sideB, members]);
  const rates = matchRates(derived);
  const authoritative = hasRole(member, "admin");

  /**
   * An organiser runs events where they are scorekeeper rather than player, so they can log
   * a match they are not in. `log_match` has always allowed this; only this screen did not,
   * because it seeded the roster with you and gave you no way out of it.
   *
   * You are still pre-filled, because playing in the match you are logging is the ordinary
   * case and pre-filling saves a tap. The difference is that now you can remove yourself.
   */
  const canLogForOthers = hasRole(member, "organiser");
  const playing = member !== null && sideA.includes(member.id);

  const submit = useMutation({
    mutationFn: async () => {
      const matchId = await logMatch({ kind, games, sideA, sideB });
      // An admin's entry is authoritative, so it rates on the spot rather than
      // waiting on a tap from someone who has already gone home. The rating write
      // still goes through the Edge Function — there is one code path for the maths.
      if (authoritative) await confirmMatch(matchId);
      return matchId;
    },
    onSuccess: () => {
      writeLastKind(kind);
      writeLastFormat(format);
      void queryClient.invalidateQueries({ queryKey: ["my-matches"] });
      navigate("/matches");
    },
  });

  const onGamesChange = useCallback((next: Game[]) => setGames(next), []);

  function drop(side: "A" | "B", id: string) {
    const setter = side === "A" ? setSideA : setSideB;
    setter((current) => current.filter((memberId) => memberId !== id));
  }

  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <PageHeader eyebrow={`${SPORT_LABEL} · ${kind}`} title="Log a match" />

      {step === "setup" ? (
        <>
          <div className="micro mb-2">Discipline</div>
          <div className="mb-8">
            <Chips
              options={[
                { value: "doubles" as Kind, label: "Doubles" },
                { value: "singles" as Kind, label: "Singles" },
              ]}
              selected={[kind]}
              onToggle={(value) => {
                setKind(value);
                // Changing discipline clears the roster because the side sizes change. Keep
                // whether you had put yourself in it — an organiser who just took
                // themselves out should not have to do it again.
                setSideA(member && playing ? [member.id] : []);
                setSideB([]);
              }}
            />
          </div>

          <div className="micro mb-2">Format</div>
          <div className="mb-8">
            <Chips
              options={[
                ...FORMATS.map((f) => ({ value: formatKey(f), label: formatChip(f) })),
                { value: CUSTOM, label: "Custom" },
              ]}
              selected={[custom ? CUSTOM : formatKey(format)]}
              onToggle={(value) => {
                if (value === CUSTOM) {
                  // Seeded from whatever was showing, so Custom starts from the format you
                  // were already looking at rather than from nothing.
                  setCustom(true);
                  return;
                }
                const found = FORMATS.find((f) => formatKey(f) === value);
                if (found) {
                  setCustom(false);
                  setFormat(found);
                }
              }}
            />

            {custom ? (
              <div className="mt-4 flex items-end gap-3">
                <NumberField
                  label="Games"
                  value={format.bestOf}
                  min={1}
                  max={7}
                  onChange={(bestOf) => setFormat((f) => ({ ...f, bestOf }))}
                />
                <span className="pb-3 text-ink-muted">×</span>
                <NumberField
                  label="Points"
                  value={format.target}
                  min={1}
                  max={99}
                  onChange={(target) => setFormat((f) => ({ ...f, target }))}
                />
              </div>
            ) : null}
          </div>

          <div className="micro mb-2">Match type</div>
          <div className="mb-8">
            <Chips
              options={[
                { value: "any" as const, label: "Any" },
                { value: "men" as const, label: "Men's" },
                { value: "women" as const, label: "Women's" },
                { value: "mixed" as const, label: "Mixed" },
              ]}
              selected={[wanted]}
              onToggle={(value) => {
                setWanted(value);
                // The roster is cleared because the people on it may no longer be offered,
                // and a hidden participant you cannot see or remove is worse than retyping.
                setSideA(
                  member &&
                    playing &&
                    (value === "any" || value === "mixed" || member.category === value)
                    ? [member.id]
                    : [],
                );
                setSideB([]);
              }}
            />
            <p className="micro mt-2" style={{ textTransform: "none", letterSpacing: 0 }}>
              Filters who you can pick. The match is recorded as whatever the players actually are,
              and a mixed one does not affect ratings.
            </p>
          </div>

          <div className="tramline mb-6" />

          <SideRoster
            label={playing ? "Your side" : "Side A"}
            ids={sideA}
            perSide={perSide}
            nameOf={nameOf}
            // Locked for a player: entering someone else's match is not theirs to do, and
            // log_match would refuse it anyway. Unlocked for an organiser, who is often
            // running the court rather than on it.
            lockedId={canLogForOthers ? undefined : member?.id}
            onRemove={(id) => drop("A", id)}
          />
          {sideA.length < perSide ? (
            <div className="mb-8">
              <MemberPicker
                label={playing ? "Your partner" : sideA.length === 0 ? "First player" : "Partner"}
                members={eligible}
                chosen={[...sideA, ...sideB]}
                recent={recent}
                onPick={(id) => setSideA((current) => [...current, id])}
              />
            </div>
          ) : null}

          <SideRoster
            label={playing ? "Opponents" : "Side B"}
            ids={sideB}
            perSide={perSide}
            nameOf={nameOf}
            onRemove={(id) => drop("B", id)}
          />
          {sideB.length < perSide ? (
            <div className="mb-8">
              <MemberPicker
                label={
                  playing
                    ? sideB.length === 0
                      ? "First opponent"
                      : "Second opponent"
                    : sideB.length === 0
                      ? "First player"
                      : "Partner"
                }
                members={eligible}
                chosen={[...sideA, ...sideB]}
                recent={recent}
                onPick={(id) => setSideB((current) => [...current, id])}
              />
            </div>
          ) : null}

          <Button
            tone="primary"
            className="w-full"
            disabled={!rosterReady}
            onClick={() => setStep("score")}
          >
            Enter the score
          </Button>
        </>
      ) : null}

      {step === "score" ? (
        <>
          <ScorePad
            nameA={teamName(sideA)}
            nameB={teamName(sideB)}
            format={format}
            onGamesChange={onGamesChange}
            onDone={() => setStep("review")}
          />
          <button
            type="button"
            onClick={() => setStep("setup")}
            className="micro mt-6 underline"
            style={{ minHeight: 44 }}
          >
            Back to players
          </button>
        </>
      ) : null}

      {step === "review" ? (
        <>
          <div className="border-t-2 py-4" style={{ borderColor: "var(--color-line)" }}>
            <div className="mb-1">{teamName(sideA)}</div>
            <div className="text-ink-muted">vs {teamName(sideB)}</div>
            <div className="font-mono mt-3 text-lg">{formatScoreline(games)}</div>
            <div className="micro mt-2">
              {winnerName(games, teamName(sideA), teamName(sideB))} won
            </div>
            {/* What it will actually be recorded as, from the same rule the database
                applies. Set up a women's match, have an admin add a man, and you see it
                become Mixed here rather than discovering it afterwards. */}
            <div className="micro mt-2">
              {derived
                ? `Recorded as ${MATCH_CATEGORY_LABEL[derived].toLowerCase()} ${kind}`
                : `Recorded as ${kind} — category unknown, someone here has none set`}
            </div>
          </div>

          {/* Before the tap, not after it. Someone who finds out their evening of mixed
              doubles moved nothing has been told too late to choose differently. */}
          <Notice tone={rates ? "muted" : "attention"}>
            {!rates
              ? "A mixed match is recorded and shows in everyone’s history, but it does not change any rating."
              : authoritative
                ? "You're an admin, so this counts straight away — no confirmation needed."
                : "Logged matches wait on an opponent to confirm before they change anyone’s rating."}
          </Notice>

          <div className="flex gap-3">
            <Button tone="primary" onClick={() => submit.mutate()} disabled={submit.isPending}>
              {submit.isPending
                ? "Logging…"
                : authoritative && rates
                  ? "Log and rate"
                  : "Log match"}
            </Button>
            <Button onClick={() => setStep("score")}>Fix the score</Button>
          </div>

          {submit.error ? <Notice tone="attention">{submit.error.message}</Notice> : null}
        </>
      ) : null}
    </main>
  );
}

function SideRoster({
  label,
  ids,
  perSide,
  nameOf,
  lockedId,
  onRemove,
}: {
  label: string;
  ids: string[];
  perSide: number;
  nameOf: (id: string) => string;
  lockedId?: string;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="mb-4">
      <div className="micro mb-2">
        {label} · {ids.length}/{perSide}
      </div>
      <ul>
        {ids.map((id) => (
          <li
            key={id}
            className="flex items-center justify-between border-t-2 py-2"
            style={{ borderColor: "var(--color-line)" }}
          >
            <span>{nameOf(id)}</span>
            {id === lockedId ? (
              <span className="micro">you</span>
            ) : (
              <button
                type="button"
                onClick={() => onRemove(id)}
                className="micro underline"
                style={{ minHeight: 44, paddingLeft: 12 }}
              >
                Remove
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function winnerName(games: Game[], nameA: string, nameB: string): string {
  let a = 0;
  let b = 0;
  for (const game of games) {
    if (game.a > game.b) a++;
    else b++;
  }
  return a > b ? nameA : nameB;
}

/** `3x21`. Small enough to keep in one localStorage value alongside the discipline. */
const formatKey = (f: MatchFormat) => `${f.bestOf}x${f.target}`;

const CUSTOM = "custom";

/** Whether this format is one of the sport's presets, or something typed in. */
const isPreset = (f: MatchFormat) => FORMATS.some((p) => formatKey(p) === formatKey(f));

/**
 * A number the way it is entered courtside: a box you tap and type into, not a stepper you
 * press ten times. `inputMode="numeric"` brings up the digit keypad on a phone.
 */
function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="flex-1">
      <span className="field-label">{label}</span>
      <input
        type="number"
        inputMode="numeric"
        value={value}
        min={min}
        max={max}
        onChange={(event) => {
          const next = Number(event.target.value);
          // Clamped rather than validated on submit: a match of 0 games has no winner, and
          // log_match refuses more than 7 anyway. Better to make it unenterable.
          if (Number.isFinite(next)) onChange(Math.min(Math.max(Math.round(next), min), max));
        }}
        style={{ ...inputStyle, textAlign: "center" }}
      />
    </label>
  );
}
const formatChip = (f: MatchFormat) =>
  f.bestOf === 1 ? `1 × ${f.target}` : `${f.bestOf} × ${f.target}`;

const LAST_FORMAT_KEY = "rally.lastFormat";

function readLastFormat(): MatchFormat {
  try {
    const raw = localStorage.getItem(LAST_FORMAT_KEY);
    const found = FORMATS.find((f) => formatKey(f) === raw);
    if (found) return found;
    // A custom format is remembered too — a club that plays 5 × 10 every week should not
    // have to retype it every week.
    const custom = /^(\d+)x(\d+)$/.exec(raw ?? "");
    if (custom) return { bestOf: Number(custom[1]), target: Number(custom[2]) };
  } catch {
    // A corrupt preference is not worth a broken screen.
  }
  return FORMATS[0]!;
}

function writeLastFormat(format: MatchFormat): void {
  try {
    localStorage.setItem(LAST_FORMAT_KEY, formatKey(format));
  } catch {
    // Private browsing. The default is fine.
  }
}

function readLastKind(): Kind {
  try {
    const raw = localStorage.getItem(LAST_USED_KEY);
    if (raw === "singles" || raw === "doubles") return raw;
  } catch {
    // A corrupt preference is not worth a broken screen.
  }
  return "doubles";
}

function writeLastKind(kind: Kind): void {
  try {
    localStorage.setItem(LAST_USED_KEY, kind);
  } catch {
    // Private browsing. The default is fine.
  }
}
